/* Dialling codes for the phone field.
 *
 * The web half of app/mobile/lib/core/countries.dart. THE TWO LISTS MUST STAY
 * IN STEP — somebody who registers on the app and signs in on the web has to
 * find their country in both. If you add a row here, add it there.
 *
 * NOT EVERY COUNTRY. About twenty, chosen for where this app's people actually
 * are: India, its neighbours, and the Gulf and Anglophone diaspora where these
 * congregations have members. The full ITU list is ~240 entries, and scrolling
 * past Andorra and Azerbaijan to reach the UAE is a worse experience than not
 * having some of them at all.
 *
 * If somebody needs a country that is missing, it is one line — and the server
 * does not care either way: normalize_mobile accepts any E.164 prefix, and its
 * `+91` default only applies when no country code is given at all.
 *
 * The flags are Unicode regional-indicator pairs rather than images, so there
 * is nothing to bundle and nothing to fail to load.
 */
export type Country = {
  /** ISO 3166-1 alpha-2, and the stable key. Two countries can share a dial
   *  code (+1 is US and Canada), so the dial code cannot identify a row. */
  code: string
  dial: string
  name: string
  flag: string
  /** How many digits the national number has, for the hint and for a check
   *  that catches a typo before the request is sent. A country with several
   *  valid lengths gets its most common one; the server's E.164 check is the
   *  real gate, this is only there to save a round trip. */
  digits: number
}

/** India. The default everywhere, and the fallback when a stored code no
 *  longer matches a row — better than undefined and a crash. */
export const INDIA: Country = {
  code: 'IN', dial: '+91', name: 'India', flag: '🇮🇳', digits: 10,
}

export const COUNTRIES: Country[] = [
  INDIA,
  // The Gulf — the largest Indian diaspora.
  { code: 'AE', dial: '+971', name: 'United Arab Emirates', flag: '🇦🇪', digits: 9 },
  { code: 'SA', dial: '+966', name: 'Saudi Arabia', flag: '🇸🇦', digits: 9 },
  { code: 'QA', dial: '+974', name: 'Qatar', flag: '🇶🇦', digits: 8 },
  { code: 'KW', dial: '+965', name: 'Kuwait', flag: '🇰🇼', digits: 8 },
  { code: 'OM', dial: '+968', name: 'Oman', flag: '🇴🇲', digits: 8 },
  { code: 'BH', dial: '+973', name: 'Bahrain', flag: '🇧🇭', digits: 8 },
  // Anglophone diaspora.
  { code: 'US', dial: '+1', name: 'United States', flag: '🇺🇸', digits: 10 },
  { code: 'CA', dial: '+1', name: 'Canada', flag: '🇨🇦', digits: 10 },
  { code: 'GB', dial: '+44', name: 'United Kingdom', flag: '🇬🇧', digits: 10 },
  { code: 'AU', dial: '+61', name: 'Australia', flag: '🇦🇺', digits: 9 },
  { code: 'NZ', dial: '+64', name: 'New Zealand', flag: '🇳🇿', digits: 9 },
  { code: 'IE', dial: '+353', name: 'Ireland', flag: '🇮🇪', digits: 9 },
  // Near neighbours.
  { code: 'SG', dial: '+65', name: 'Singapore', flag: '🇸🇬', digits: 8 },
  { code: 'MY', dial: '+60', name: 'Malaysia', flag: '🇲🇾', digits: 9 },
  { code: 'LK', dial: '+94', name: 'Sri Lanka', flag: '🇱🇰', digits: 9 },
  { code: 'NP', dial: '+977', name: 'Nepal', flag: '🇳🇵', digits: 10 },
  { code: 'BD', dial: '+880', name: 'Bangladesh', flag: '🇧🇩', digits: 10 },
  { code: 'PK', dial: '+92', name: 'Pakistan', flag: '🇵🇰', digits: 10 },
]

export const DEFAULT_COUNTRY = INDIA

export function countryByCode(code: string | null | undefined): Country {
  return COUNTRIES.find((c) => c.code === code) ?? DEFAULT_COUNTRY
}

/** What goes to the API. The ONLY place E.164 is assembled on the web.
 *
 *  It exists because the old signup did NOT do this: the field showed a static
 *  "+91" and then submitted the bare digits beside it. That happened to work
 *  for India — the server defaults a bare number to +91 — and was unfixable for
 *  everyone else, because there was no way to say any other country. */
export function toE164(country: Country, national: string): string {
  return `${country.dial}${national.replace(/\D/g, '')}`
}

/** For display back to the person — "+91 9876543210". */
export function prettyPhone(country: Country, national: string): string {
  return `${country.dial} ${national}`
}

/** Search by name, dial code with or without the '+', or ISO code. People
 *  type "uae", "971" and "+971", and all three should find the same row. */
export function searchCountries(query: string): Country[] {
  const q = query.trim().toLowerCase()
  if (!q) return COUNTRIES
  return COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      c.dial.includes(q) ||
      c.dial.slice(1).startsWith(q) ||
      c.code.toLowerCase() === q,
  )
}
